import { addCacheSuffixToURL } from "common/utils/assetFetcher";
import { fetchArrayBufferWithRetry, memoProxyFetch } from "common/utils/fetchHelpers";
import type { AssetLoader } from "doc/plans/assetLoader";
import type { Vec2 } from "geom/vec2";
import { parseDXFContent } from "lib/house/dxf/import/parseDXF";

export class BrowserAssetLoader implements AssetLoader {
  constructor(private readonly webworkerFetchImageSize?: (url: string) => Promise<Vec2>) {}

  fetchArrayBuffer = memoProxyFetch(async (url: string) => {
    return await fetchArrayBufferWithRetry(addCacheSuffixToURL(url), 5);
  });

  fetchBlob = memoProxyFetch(async (url: string) => {
    const buff = await this.fetchArrayBuffer(url);
    return new Blob([buff], { type: "image/png" });
  });

  fetchText = memoProxyFetch(async (url: string) => {
    return (await fetch(addCacheSuffixToURL(url))).text();
  });

  fetchImageSize = memoProxyFetch(async (url: string) => {
    if (this.webworkerFetchImageSize) {
      return this.webworkerFetchImageSize(url);
    }

    const blob = await this.fetchBlob(url);
    const objURL = URL.createObjectURL(blob);

    const img = new Image();

    return new Promise<[number, number]>((resolve, reject) => {
      img.onload = () => {
        const { width, height } = img;

        return resolve([width, height]);
      };

      img.onerror = () => {
        return reject(`Unable to get image size (url: '${url}')`);
      };

      img.src = objURL;
    });
  });

  fetchDXF = memoProxyFetch(async (url: string) => {
    const dxfString = await this.fetchText(url);
    return parseDXFContent(dxfString);
  });

  fetchWasm = async () => {
    const wasm = await import("wasm");
    const res = await wasm.default();

    return { wasm, memory: res.memory };
  };
}
